import { useState } from 'react';
import { History, RotateCcw, X } from 'lucide-react';
import { useToleranceStore } from '../stores/toleranceStore';
import { useProjectStore } from '../stores/projectStore';
import { useAuthStore } from '../stores/authStore';
import { loadRecovery, clearRecovery } from '../lib/recovery';
import { toast } from '../lib/toast';

// Menschenlesbarer relativer Zeitstempel ohne externe i18n-Lib.
// Deckt den Bereich ab, der für Recovery realistisch ist: Sekunden
// bis wenige Tage nach dem letzten Edit. Fallback ist das volle
// Datum bei längeren Lücken.
function formatRelativeTime(ts) {
  const secs = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (secs < 60) return 'vor wenigen Sekunden';
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    return `vor ${m} Minute${m === 1 ? '' : 'n'}`;
  }
  if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    return `vor ${h} Stunde${h === 1 ? '' : 'n'}`;
  }
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

// Crash-/Reload-Recovery-Banner (Task #35). Erscheint direkt unter
// dem SubBar, wenn:
//   1. localStorage einen Recovery-Snapshot mit Elementen hat,
//   2. die aktuelle Berechnung im Store leer ist (sonst wäre ein
//      Banner irreführend — der User hat schon was geladen),
//   3. kein Cloud-Projekt aktiv ist (das ist die echte Quelle und
//      würde gegen den Recovery-Snapshot konkurrieren).
//
// User-Aktionen: "Wiederherstellen" lädt den Snapshot via loadProject;
// "Verwerfen" löscht den localStorage-Eintrag. Beide Aktionen
// schließen das Banner; der useRecoveryAutoSave-Hook schreibt
// anschließend den nun aktuellen Zustand wieder ohne Lücke.
export default function RecoveryBanner() {
  const elements = useToleranceStore((s) => s.elements);
  const loadProject = useToleranceStore((s) => s.loadProject);
  const currentProject = useProjectStore((s) => s.currentProject);
  // Nur der eigene Snapshot wird angeboten. Hinter AuthGuard ist die id
  // beim ersten Render da; ein fremder Snapshot (anderer Account am selben
  // Browser) wird in loadRecovery verworfen und gelöscht.
  const userId = useAuthStore((s) => s.user?.id ?? null);

  // Lazy-Init im useState-Initializer statt im useEffect, damit das
  // Lesen synchron beim ersten Render passiert — VOR irgendeinem
  // useEffect, der parallel im Tree läuft. Ohne diesen Trick konnte
  // useRecoveryAutoSave (anderer Hook im selben CalculatorApp-Mount)
  // beim Boot mit leeren elements zuerst clearRecovery() aufrufen
  // und uns nichts mehr zum Lesen übrig lassen.
  const [snapshot] = useState(() => loadRecovery(userId));
  const [hidden, setHidden] = useState(false);

  if (hidden || !snapshot) return null;
  // Wenn der Calculator schon einen Inhalt hat (Pro mit aktiver Calc,
  // Template geladen, .tcp importiert), ist Recovery nicht sinnvoll —
  // der User hat bewusst gewählt was er sieht. Schalter NICHT clearen,
  // damit ein späteres "Neu starten" das Banner doch noch anbieten kann.
  if (elements.length > 0 || currentProject) return null;

  const handleRestore = () => {
    loadProject(
      {
        elements: snapshot.elements,
        m0: snapshot.m0,
        mode: snapshot.mode,
        mcSamples: snapshot.mcSamples,
        mcConfidence: snapshot.mcConfidence,
        mcSeed: snapshot.mcSeed,
        thermal: snapshot.thermal,
      },
      snapshot.calculationName || 'Wiederhergestellte Berechnung',
      null,
      null,
      { proMode: true, startCounted: true }
    );
    clearRecovery();
    setHidden(true);
    toast.success('Letzte Sitzung wiederhergestellt');
  };

  const handleDismiss = () => {
    clearRecovery();
    setHidden(true);
  };

  const elementWord = snapshot.elements.length === 1 ? 'Maß' : 'Maße';

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-brand-soft border-b border-brand/30"
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px' }}
    >
      <History size={16} className="text-brand shrink-0" aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span className="text-brand" style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
          Letzte Sitzung wiederherstellen?
        </span>
        <span className="text-ink-2" style={{ fontSize: 11, lineHeight: 1.3 }}>
          „{snapshot.calculationName || 'Unbenannt'}" mit {snapshot.elements.length} {elementWord}
          {' · '}
          <span className="num">{formatRelativeTime(snapshot.savedAt)}</span>
        </span>
      </div>
      <button
        type="button"
        onClick={handleRestore}
        className="bg-brand text-white border border-brand shadow-sm hover:bg-[var(--color-brand-2)] inline-flex items-center gap-1.5"
        style={{ padding: '6px 12px', fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
      >
        <RotateCcw size={12} aria-hidden="true" />
        Wiederherstellen
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        title="Verwerfen - der Snapshot wird gelöscht"
        className="text-ink-3 hover:text-ink-2 inline-flex items-center gap-1"
        style={{ padding: '6px 10px', fontSize: 11.5, fontWeight: 600, border: 'none', background: 'transparent', cursor: 'pointer' }}
      >
        <X size={12} aria-hidden="true" />
        Verwerfen
      </button>
    </div>
  );
}
